<script setup lang="ts">
import { ArrowUpRight, Loader2, Lock } from 'lucide-vue-next'

import { getMethodMeta } from '@/views/loyalty/components/recovery/methods'
import { useRecoveryConfig } from '@/views/loyalty/components/recovery/useRecoveryConfig'

const { t } = useI18n()
const { defaultSelection, exceptions, isLoading, loadFailed, ensureLoaded, load } = useRecoveryConfig()

onMounted(() => {
  ensureLoaded()
})

const activeMethods = computed(() => defaultSelection.methods.map((key) => getMethodMeta(key)))
</script>

<template>
  <div class="flex flex-col gap-4">
    <BaseTip severity="secondary" class="items-center [&>svg]:translate-y-0">
      <template #icon>
        <Lock :stroke-width="2.5" />
      </template>
      <template #text>
        {{ t('pages.loyaltySupports.webapp.editor.recovery.infoOnly') }}
      </template>
      <template #button>
        <Button variant="outline" size="sm" as-child>
          <RouterLink :to="{ name: 'LoyaltyEdit', query: { activeTab: 'settings' } }" target="_blank" rel="noopener">
            {{ t('pages.loyaltySupports.webapp.editor.recovery.edit') }}
            <ArrowUpRight />
          </RouterLink>
        </Button>
      </template>
    </BaseTip>

    <div v-if="isLoading" class="text-muted-foreground flex items-center gap-2 text-sm">
      <Loader2 class="size-4 animate-spin" />
      {{ t('pages.loyaltySupports.webapp.editor.recovery.loading') }}
    </div>

    <div v-else-if="loadFailed" class="flex flex-col items-start gap-2">
      <p class="text-destructive text-sm">{{ t('pages.loyaltySupports.webapp.editor.recovery.loadFailed') }}</p>
      <Button variant="outline" size="sm" @click="load">{{
        t('pages.loyaltySupports.webapp.editor.recovery.retry')
      }}</Button>
    </div>

    <div v-else class="flex flex-col gap-3">
      <p class="text-foreground text-sm font-medium">
        {{ t('pages.loyaltySupports.webapp.editor.recovery.activeTitle') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <BaseChip
          v-for="method in activeMethods"
          :key="method.key"
          color="slate"
          :text="t(`pages.loyaltyPage.tabs.recovery.methods.${method.key}.label`)"
        >
          <template #leading>
            <component :is="method.icon" class="size-3.5" />
          </template>
        </BaseChip>
      </div>

      <p v-if="exceptions.length > 0" class="text-muted-foreground text-xs">
        {{ t('pages.loyaltySupports.webapp.editor.recovery.exceptions', { count: exceptions.length }) }}
      </p>
    </div>
  </div>
</template>
